import { Injectable } from "@angular/core";
import { Subject } from "rxjs/Subject";
import { Observable } from "rxjs/Observable";
import * as io from "socket.io-client";
import { environment } from "../../../environments/environment";
import { PlanningPokerParticipant } from "../../interfaces/planningPokerParticipant";
import { ReplaySubject, Subscription } from "rxjs";
import { UUID } from "angular2-uuid";
import { SessionService } from "../session/session.service";

@Injectable()
export class PlanningPokerService {
  private socket: io;
  private env: any = environment;
  socketIoToken: string;

  average: number;
  median: number;
  agreement: number;
  consensus: number;
  variation: number;

  selectedValue: string;

  planningOpen: boolean;
  planningClosed: boolean;
  currentFeatureId: string;

  reconnected: Observable<any>;
  disconnected: Observable<any>;
  estimationStarted: Observable<any>;
  estimationStopped: Observable<any>;
  participantJoined: Observable<PlanningPokerParticipant>;
  participantLeft: Observable<PlanningPokerParticipant>;
  estimationSet: Observable<PlanningPokerParticipant>;
  consensusSet: Observable<any>;
  setStatus: Observable<any>;

  participantJoinedSubscription: Subscription;
  participantLeftSubscription: Subscription;
  estimationSetSubscription: Subscription;
  consensusSetSubscription: Subscription;
  setStatusSubscription: Subscription;

  participants: Map<string, PlanningPokerParticipant> = new Map<
    string,
    PlanningPokerParticipant
  >();
  private participantsSubject: Subject<
    PlanningPokerParticipant[]
  > = new ReplaySubject<PlanningPokerParticipant[]>();
  participantsObservable: Observable<
    PlanningPokerParticipant[]
  > = this.participantsSubject.asObservable();

  constructor(private sessionService: SessionService) {
    this.socketIoToken =
      sessionStorage.getItem("SocketIoToken") || this.uuidv4();
    sessionStorage.setItem("SocketIoToken", this.socketIoToken);
    this.socket = io(this.env.socketUrl + "/planning-poker", {
      path: this.env.socketPath,
      reconnect: true,
      secure: true,
      transports: ["websocket"],
    });
    this.estimationStarted = this.registerMessage<any>("estimationStarted");
    this.estimationStopped = this.registerMessage<any>("estimationStopped");
    this.participantJoined = this.registerMessage<PlanningPokerParticipant>(
      "participantJoined"
    );
    this.estimationSet = this.registerMessage<PlanningPokerParticipant>(
      "estimationSet"
    );
    this.consensusSet = this.registerMessage<PlanningPokerParticipant>(
      "consensusSet"
    );
    this.participantLeft = this.registerMessage<PlanningPokerParticipant>(
      "participantLeft"
    );
    this.reconnected = this.registerMessage<any>("reconnect");
    this.disconnected = this.registerMessage<any>("disconnect");
    this.setStatus = this.registerMessage<any>("setStatus");

    this.loadStatus();
  }

  private saveStatus() {
    this.sessionService.setPlanningPokerStatus({
      average: this.average,
      median: this.median,
      agreement: this.agreement,
      consensus: this.consensus,
      variation: this.variation,
      selectedValue: this.selectedValue,
      planningOpen: this.planningOpen,
      planningClosed: this.planningClosed,
      currentFeatureId: this.currentFeatureId,
      participants: Array.from(this.participants.entries()),
    });
  }

  private loadStatus() {
    let status = this.sessionService.getPlanningPokerStatus();
    if (status) {
      this.average = status.average;
      this.median = status.median;
      this.agreement = status.agreement;
      this.consensus = status.consensus;
      this.variation = status.variation;
      this.selectedValue = status.selectedValue;
      this.planningOpen = status.planningOpen;
      this.planningClosed = status.planningClosed;
      this.currentFeatureId = status.currentFeatureId;
      this.participants = new Map<string, PlanningPokerParticipant>(
        status.participants
      );
    }
  }

  private uuidv4() {
    return UUID.UUID();
  }
  private registerMessage<T>(message: string): Observable<T> {
    let subject = new Subject<T>();
    this.socket.on(message, (x: T) => {
      subject.next(x);
    });
    return subject;
  }

  private emitWithCallback<T>(message: string, data: any): Promise<T> {
    let subject = new Subject<T>();
    data["SocketIoToken"] = this.socketIoToken;
    this.socket.emit(
      message,
      data,
      (response: { success: boolean; result: T; error: string }) => {
        if (response.success) {
          subject.next(response.result);
          subject.complete();
        } else {
          subject.error(response.error);
        }
      }
    );
    return subject.take(1).toPromise();
  }

  async watchProject(shortId: string): Promise<PlanningPokerParticipant[]> {
    this.participantJoinedSubscription = this.participantJoined.subscribe(
      (participant) => {
        if (!this.participants.has(participant.token)) {
          this.participants.set(participant.token, participant);
          this.participantsSubject.next(Array.from(this.participants.values()));
          this.saveStatus();
        }
      }
    );

    this.participantLeftSubscription = this.participantLeft.subscribe(
      (participant) => {
        this.participants.delete(participant.token);
        this.participantsSubject.next(Array.from(this.participants.values()));
        this.saveStatus();
      }
    );

    this.estimationSetSubscription = this.estimationSet.subscribe(
      (participant) => {
        this.participants.get(participant.token).estimation =
          participant.estimation;
        this.saveStatus();
      }
    );

    this.setStatusSubscription = this.setStatus.subscribe((result) => {
      this.doSetStatus(result.result);
    });

    this.consensusSetSubscription = this.consensusSet.subscribe((consensus) => {
      this.consensus = consensus;
      this.saveStatus();
    });

    let result = await this.emitWithCallback<any>("watchProject", {
      projectId: shortId,
    });
    this.doSetStatus(result);
    return result;
  }

  private doSetStatus(result: any) {
    this.participants.clear();
    result.participants.forEach((x) => this.participants.set(x.token, x));
    this.participantsSubject.next(Array.from(this.participants.values()));
    this.planningOpen = result.status === 3;
    this.planningClosed = !this.planningOpen;
    this.currentFeatureId = result.featureId;
    var values = Array.from(this.participants.values())
      .map((x) => Number(x.estimation))
      .filter((x) => !isNaN(x));
    if (values.length > 0) {
      this.average = this.calcAverage(values);
      this.median = this.calcMedian(values);
      this.agreement = this.calcAgreement(values);
      this.variation = this.calcVariation(values);
    } else {
      this.average = null;
      this.median = null;
      this.agreement = null;
      this.variation = null;
    }
    this.participantsSubject.next(Array.from(this.participants.values()));
    this.saveStatus();
  }

  async removeParticipant(token: string): Promise<any> {
    return this.emitWithCallback<any>("removeParticipant", { token: token });
  }

  async stopWatchingProject(shortId: string): Promise<any> {
    this.participantJoinedSubscription.unsubscribe();
    this.participantLeftSubscription.unsubscribe();
    return this.emitWithCallback<any>("stopWatchingProject", {
      projectId: shortId,
    });
  }

  async joinProject(
    shortId: string,
    name: string,
    password: string
  ): Promise<any> {
    return this.emitWithCallback<any>("joinProject", {
      projectId: shortId,
      name: name,
      password: password,
    });
  }

  async startEstimation(featureShortId: string): Promise<any> {
    let result = await this.emitWithCallback<any>("startEstimation", {
      featureId: featureShortId,
    });
    Array.from(this.participants.values()).forEach((x) => delete x.estimation);
    this.currentFeatureId = featureShortId;
    this.planningClosed = false;
    this.planningOpen = true;
    this.average = null;
    this.median = null;
    this.agreement = null;
    this.variation = null;
    this.saveStatus();
    return result;
  }

  async setConsensus(
    featureShortId: string,
    consensus: number
  ): Promise<boolean> {
    let result = await this.emitWithCallback<any>("setConsensus", {
      featureId: featureShortId,
      consensus: consensus,
    });
    this.consensus = consensus;
    this.saveStatus();
    return true;
  }

  async stopEstimation(featureShortId: string): Promise<boolean> {
    // if (Array.from(this.participants.values()).filter(x => !x.estimation).length > 0 &&
    //   !confirm('Nicht alle Teilnehmer haben einen Wert geschätzt. Willst Du die Schätzrunde wirklich beenden?'))
    //   return false;
    var values = Array.from(this.participants.values())
      .map((x) => Number(x.estimation))
      .filter((x) => !isNaN(x));
    if (values.length > 0) {
      this.average = this.calcAverage(values);
      this.median = this.calcMedian(values);
      this.agreement = this.calcAgreement(values);
      this.variation = this.calcVariation(values);
    } else {
      this.average = null;
      this.median = null;
      this.agreement = null;
      this.variation = null;
    }
    let result = await this.emitWithCallback<any>("stopEstimation", {
      featureId: featureShortId,
      median: this.median,
      average: this.average,
      agreement: this.agreement,
      variation: this.variation,
    });
    this.planningClosed = true;
    this.planningOpen = false;
    this.saveStatus();
    return true;
  }

  async setEstimation(value: string): Promise<any> {
    return this.emitWithCallback<any>("setEstimation", { value: value });
  }

  private calcAverage(values: Array<number>): number {
    var sum = values.reduce(function (a, b) {
      return a + b;
    });
    return sum / values.length;
  }

  private calcAgreement(values: Array<number>): number {
    let average = values.reduce((a, b) => a + b, 0) / values.length;
    let variance =
      values.map((x) => Math.pow(x - average, 2)).reduce((a, b) => a + b, 0) /
      values.length;
    let stdDeviation = Math.sqrt(variance);
    let coefficient = stdDeviation / average;
    return coefficient;
  }

  private calcMedian(values: Array<number>): number {
    values.sort(function (a, b) {
      return a - b;
    });

    if (values.length === 0) return 0;

    var half = Math.floor(values.length / 2);

    if (values.length % 2) return values[half];
    else return (values[half - 1] + values[half]) / 2.0;
  }

  private calcVariation(values: Array<number>): number {
    return (
      (Math.sqrt(
        values
          .map((x) => Math.pow(x - this.average, 2))
          .reduce((x, y) => x + y, 0)
      ) /
        values.length /
        this.average) *
      100
    );
  }
}



// WEBPACK FOOTER //
// /agir/src/app/services/planning-poker/planning-poker.service.ts